import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = ["EMAIL", "SMS", "WHATSAPP", "PUSH", "INAPP"];

function ChannelList({ channel }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", channel, subject: "", body: "", variables: [], active: true });

  const load = async () => { const { data } = await api.get("/admin/notifications/templates", { params: { channel } }); setItems(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channel]);

  const openCreate = () => { setEditing(null); setForm({ code: "", channel, subject: "", body: "", variables: [], active: true }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...t, variables: t.variables || [] }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/notifications/templates/${editing.id}`, form);
      else await api.post("/admin/notifications/templates", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (t) => { if (window.confirm(`Delete template ${t.code}?`)) { await api.delete(`/admin/notifications/templates/${t.id}`); load(); } };

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={openCreate} data-testid={`${channel.toLowerCase()}-add`}><Plus className="w-4 h-4 mr-1" /> New template</Button></div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((t) => (
          <Card key={t.id} data-testid={`${channel.toLowerCase()}-tpl-${t.code}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-semibold font-mono text-sm">{t.code}</div>
                <div className="space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(t)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {t.subject && <div className="text-xs text-muted-foreground">Subject: {t.subject}</div>}
              <pre className="text-[11px] bg-muted/40 p-2 rounded overflow-x-auto whitespace-pre-wrap">{t.body}</pre>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center text-muted-foreground py-10 col-span-2">No {channel.toLowerCase()} templates yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit ${channel} template` : `New ${channel} template`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. invoice_overdue" /></div>
            {channel === "EMAIL" && <div><Label>Subject</Label><Input value={form.subject || ""} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>}
            <div><Label>Body / template</Label><textarea className="w-full border rounded p-2 text-sm" rows="6" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> <Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>{editing ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Notifications() {
  const [channel, setChannel] = useState("EMAIL");
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-notifications">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Notifications center</h1>
        <p className="text-muted-foreground">Template library for Email, SMS, WhatsApp, push and in-app notifications. Variables (e.g. <span className="font-mono text-xs">{"{{tenant.name}}"}</span>) are substituted at send time.</p>
      </div>
      <Tabs value={channel} onValueChange={setChannel}>
        <TabsList>
          {CHANNELS.map((c) => <TabsTrigger key={c} value={c} data-testid={`notif-tab-${c}`}>{c}</TabsTrigger>)}
        </TabsList>
        {CHANNELS.map((c) => <TabsContent key={c} value={c}><ChannelList channel={c} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
